import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { Form, FormField } from '../interfaces/form.interface';
import { setDropdownSelected } from '../utils/dropdown-data.utils';
import { DropdownData, InputType } from '../interfaces/input.interface';
import { ReactiveFormValueUtils } from '../model/reactive-form-value.utils';
import { isValidSpanishId, normalizeId } from '../utils/spanish-id.utils';

@Component({
    selector: 'app-field',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './field.component.html',
    styleUrls: ['./field.component.scss'],
})
export class FieldComponent<T> {
    @Input() field!: FormField<T>;

    @Input() model!: FormGroup;

    @Input() form!: Form<T>;

    @Input() originalModel: Record<string, unknown> = {};

    @Input() filterInputParcelas!: boolean;

    @Input() undoEnabled = false;

    @Input() debug = false;

    @Input() fieldHelpTourAnchorId = '';

    @Input() sectionName = '';

    @Input() showFieldHelpButtons = false;

    /**
     * Changes whenever dynamic field logic updates a field definition.
     * This input intentionally forces OnPush field components to re-check their template.
     */
    @Input() renderVersion = 0;
    
    public readonly inputType = InputType;

    public get describedById(): string {
        return this.hasVisibleFieldError() ? `field-error-${this.field.name}` : '';
    }

    public get isInvalid(): boolean {
        return this.hasVisibleFieldError();
    }

    public hasVisibleFieldError(): boolean {
        const control = this.model.get(this.field.name ?? '');

        return Boolean(control?.invalid && (control.touched || control.dirty));
    }

    public hasVisibleSpanishIdSuccess(): boolean {
        if (!this.isSpanishIdField()) {
            return false;
        }

        const control = this.model.get(this.field.name ?? '');
        const value = normalizeId(String(control?.value ?? ''));

        return Boolean(value && control?.valid && (control.touched || control.dirty) && isValidSpanishId(value));
    }

    public getFieldErrorMessage(): string {
        const control = this.model.get(this.field.name ?? '');
        const errors = control?.errors ?? {};

        if (typeof errors['passwordRequired'] === 'string') {
            return errors['passwordRequired'];
        }

        if (typeof errors['passwordRepeatRequired'] === 'string') {
            return errors['passwordRepeatRequired'];
        }

        if (typeof errors['passwordMismatch'] === 'string') {
            return errors['passwordMismatch'];
        }

        if (Boolean(errors['required'])) {
            return 'Este campo es obligatorio.';
        }

        if (Boolean(errors['maxlength'])) {
            return 'Ha superado la longitud máxima permitida.';
        }

        if (Boolean(errors['minlength'])) {
            return 'No llega a la longitud mínima requerida.';
        }

        if (Boolean(errors['email'])) {
            return 'Introduce un correo electrónico válido.';
        }

        if (Boolean(errors['min']) || Boolean(errors['max'])) {
            return 'El valor está fuera del rango permitido.';
        }

        if (typeof errors['invalidPattern'] === 'string') {
            return errors['invalidPattern'];
        }

        if (typeof errors['repeatableRowInvalid'] === 'string') {
            return errors['repeatableRowInvalid'];
        }

        if (Boolean(errors['pattern'])) {
            return 'El formato no es válido.';
        }

        if (typeof errors['invalidTime'] === 'string') {
            return errors['invalidTime'];
        }

        if (typeof errors['invalidSpanishId'] === 'string') {
            return errors['invalidSpanishId'];
        }

        if (typeof errors['spanishIdTaken'] === 'string') {
            return errors['spanishIdTaken'];
        }

        return 'Revisa el valor introducido.';
    }

    public isFieldEdited(field: FormField<T>): boolean {
        const originalValue = this.originalModel[field.name ?? ''];
        const currentValue = this.model.get(field.name ?? '')?.value;

        return (field.conditions.hasUndoEnabled || this.undoEnabled)
            && !ReactiveFormValueUtils.areValuesEquivalent(currentValue, originalValue, ReactiveFormValueUtils.getFieldPrimaryKey(field));
    }

    public getEditedClass(field: FormField<T>): string {
        return this.isFieldEdited(field) ? 'edited' : '';
    }

    public isEditable(): boolean {
        return (this.form.type <= 2 || this.form.type === 6) && this.field.input.isEditable !== false;
    }

    public undoField(field: FormField<T>): void {
        const originalValue = this.originalModel[field.name ?? ''];

        this.model.patchValue({ [field.name ?? '']: originalValue });
        this.applySelected(field, originalValue);
    }

    public isRepeatableGroupDebugEnabled(): boolean {
        if (typeof window === 'undefined') {
            return false;
        }

        const params = new URLSearchParams(window.location.search);
        const queryEnabled = params.get('debug') === '1';
        const localEnabled = window.localStorage.getItem('debug') === '1';
        const sessionEnabled = window.sessionStorage.getItem('debug') === '1';

        return queryEnabled || localEnabled || sessionEnabled || this.debug;
    }

    private applySelected(field: FormField<T>, value: unknown): void {
        if (field.input.data) {
            setDropdownSelected(field.input.data as DropdownData<T>, value as T | null);
        }
    }

    private isSpanishIdField(): boolean {
        const fieldName = this.field?.name?.toLowerCase() ?? '';
        const fieldLabel = this.field?.label?.toLowerCase() ?? '';

        return fieldName.includes('nif')
            || fieldName.includes('cif')
            || fieldLabel.includes('nif')
            || fieldLabel.includes('cif');
    }
}
